import { useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { VersionContext } from "../context/VersionContext";

// This component listens for route changes and performs actions
// when the application's navigation state changes
const RouteChangeListener = () => {
  const location = useLocation();
  const { checkForUpdate, version } = useContext(VersionContext);
  const prevPathRef = useRef(location.pathname);
  // Use a ref to track if we've done an initial version check
  const initialCheckDoneRef = useRef(false);
  // Track if we're currently reloading to prevent loops
  const isReloadingRef = useRef(false);

  // Effect to handle per-page version refresh
  useEffect(() => {
    // Skip if no version yet, or if we're already reloading
    if (!version || isReloadingRef.current) {
      return;
    }

    // Normalize the pathname for storage key (remove trailing slashes, handle root)
    const normalizedPath = location.pathname === "/" ? "home" : location.pathname.replace(/\/$/, "").replace(/\//g, "_");
    const pageVersionKey = `page_v_${normalizedPath}`;
    const lastPageVersion = localStorage.getItem(pageVersionKey);

    // If this page was loaded with a different version, force a hard refresh
    if (lastPageVersion && lastPageVersion !== version) {
      console.log(`🔄 Page "${location.pathname}" needs refresh: was ${lastPageVersion}, now ${version}`);

      // Mark as reloading to prevent loops
      isReloadingRef.current = true;

      // Update the stored version BEFORE reloading to prevent infinite loops
      localStorage.setItem(pageVersionKey, version);

      // Clear any cached data for this page
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          return Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        }).catch(() => {});
      }

      // Force a hard reload of this page
      // Using reload() ensures fresh resources are fetched
      window.location.reload();
      return;
    }

    // If no version stored for this page yet, store it (first visit with this version)
    if (!lastPageVersion) {
      localStorage.setItem(pageVersionKey, version);
    }
  }, [location.pathname, version]);

  // Effect to handle version checking on navigation
  useEffect(() => {
    // Skip if this is just a re-render and not an actual path change
    if (prevPathRef.current === location.pathname) {
      return;
    }

    // Update the previous path reference
    prevPathRef.current = location.pathname;

    // Log navigation for debugging purposes (only on actual navigation)
    console.log(`📍 Navigation to: ${location.pathname}`);

    // Check for updates on initial load with a delay
    if (!initialCheckDoneRef.current) {
      initialCheckDoneRef.current = true;
      // Delay the initial check to ensure all components are mounted
      setTimeout(() => {
        checkForUpdate(true); // Force the initial check
      }, 2000);
    } else {
      // On subsequent navigations, check for updates (non-forced, respects interval)
      // This helps catch version changes when user navigates between pages
      checkForUpdate(false);
    }
  }, [location.pathname, checkForUpdate]);

  // This component doesn't render anything
  return null;
};

export default RouteChangeListener;
